import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import TransactionModal from '@/components/TransactionModal';
import TransferModal from '@/components/TransferModal';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showToast } from '@/lib/custom-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Plus, Edit, Trash2, ArrowUpRight, ArrowDownRight, PiggyBank, TrendingUp,
  Search, Calendar, Filter, X, Copy, Gem, FileText, FileSpreadsheet, Download,
  ChevronDown, ChevronUp, ArrowRightLeft, Package, Lock
} from 'lucide-react';
import { exportToPDF, exportToExcel, exportToCSV } from '@/utils/exportUtils';
import { formatCurrency } from '@/utils/currencyFormat';
import { useIsMobile } from '@/hooks/useIsMobile';
import MobileTransactions from '@/components/MobileTransactions';
import { getTodayLocalDateString } from '@/utils/dateUtils';

const Transactions = () => {
  const { token, user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [preselectedType, setPreselectedType] = useState('expense');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('date-desc');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedItemsId, setExpandedItemsId] = useState(null);

  // Transfer Modal states
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

  // Calcular saldos antes y después por cuenta para cada movimiento
  const transactionBalancesMap = useMemo(() => {
    if (!transactions || transactions.length === 0 || !accounts || accounts.length === 0) return {};
    const map = {};

    const byAccount = {};
    for (const t of transactions) {
      if (!t.account_id) continue;
      if (!byAccount[t.account_id]) byAccount[t.account_id] = [];
      byAccount[t.account_id].push(t);
    }

    for (const [accId, txList] of Object.entries(byAccount)) {
      const acc = accounts.find(a => a.id === accId);
      if (!acc) continue;

      const sorted = [...txList].sort((a, b) => {
        const dayA = (a.date || a.created_at || '').slice(0, 10);
        const dayB = (b.date || b.created_at || '').slice(0, 10);
        if (dayA !== dayB) return dayA.localeCompare(dayB);
        const createA = new Date(a.created_at || a.date || 0).getTime();
        const createB = new Date(b.created_at || b.date || 0).getTime();
        return createA - createB;
      });

      const isCredit = acc.type === 'credit_card';
      let running = parseFloat(acc.initial_balance) || 0;
      if (isCredit && running > 0) {
        running = -running;
      }

      for (const tx of sorted) {
        const isAdj = tx.is_balance_adjustment || /ajuste de saldo/i.test(tx.description || '');
        if (isAdj) {
          let prev = running;
          let next = tx.new_balance;
          if ((next === undefined || next === null) && tx.notes) {
            const match = tx.notes.match(/(?:nuevo saldo|saldo despu[ée]s):\s*([-$0-9.,]+)/i);
            if (match) {
              next = parseFloat(match[1].replace(/[$]/g, ''));
            }
          }
          if (prev !== undefined && !isNaN(prev) && next !== undefined && !isNaN(next)) {
            const p = isCredit ? -Math.abs(prev) : prev;
            const n = isCredit ? -Math.abs(next) : next;
            map[tx.id] = { prev: p, next: n };
            running = n;
            continue;
          }
        }

        const prev = running;
        const amt = parseFloat(tx.amount) || 0;
        let next = prev;
        if (tx.is_retention && tx.retention_status === 'released') {
          next = prev;
        } else if (tx.is_breakdown) {
          next = prev;
        } else if (tx.type === 'income') {
          next = prev + amt;
        } else if (tx.type === 'expense') {
          next = prev - amt;
        }
        map[tx.id] = { prev, next };
        running = next;
      }
    }

    return map;
  }, [transactions, accounts]);

  // Helper function to parse date string as local date (not UTC)
  const parseLocalDate = (dateString) => {
    if (!dateString) return new Date();

    // Handle ISO format with time (e.g., "2025-11-01T00:00:00.000Z")
    if (dateString.includes('T')) {
      dateString = dateString.split('T')[0];
    }

    const parts = dateString.split('-');
    if (parts.length !== 3) return new Date(dateString); // Fallback

    const [year, month, day] = parts.map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return new Date(dateString); // Fallback

    return new Date(year, month - 1, day);
  };

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data);
    } catch (error) {
      showToast.error('Error al cargar transacciones');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      showToast.error('Error al cargar categorías');
    }
  }, [token]);

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data);
    } catch (error) {
      showToast.error('Error al cargar cuentas');
    }
  }, [token]);

  useEffect(() => {
    fetchTransactions();
    fetchCategories();
    fetchAccounts();

    // Check for preselected type from Dashboard
    if (location.state?.preselectedType) {
      setPreselectedType(location.state.preselectedType);
      setIsModalOpen(true);
    }
  }, [token, location, fetchTransactions, fetchCategories, fetchAccounts]);

  // Listener para refrescar movimientos automáticamente en tiempo real
  useEffect(() => {
    const handleTransactionAdded = () => {
      fetchTransactions();
      fetchAccounts();
    };
    window.addEventListener('transaction-added', handleTransactionAdded);
    return () => window.removeEventListener('transaction-added', handleTransactionAdded);
  }, [fetchTransactions, fetchAccounts]);

  const [isDuplicate, setIsDuplicate] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleOpenModal = (type = 'expense', transaction = null) => {
    setPreselectedType(type);
    setEditingTransaction(transaction);
    setIsDuplicate(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingTransaction(null);
    setIsDuplicate(false);
  };

  const handleTransactionSuccess = () => {
    fetchTransactions();
    window.dispatchEvent(new CustomEvent('transaction-added'));
    handleCloseModal();
  };

  const handleDelete = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;
    try {
      await axios.delete(`${API}/transactions/${transactionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast.success('Transacción eliminada exitosamente');
      setDeleteConfirmOpen(false);
      setTransactionToDelete(null);
      fetchTransactions();
      window.dispatchEvent(new CustomEvent('transaction-added'));
    } catch (error) {
      showToast.error('Error al eliminar transacción');
    }
  };

  // Filtering and sorting logic
  const getFilteredTransactions = () => {
    let filtered = [...transactions];

    // Type filter
    if (filterType !== 'all') {
      if (filterType === 'transfer') {
        // Filter for transfers: check if category name contains "transfer"
        filtered = filtered.filter(t => {
          const category = categories.find(c => c.id === t.category_id);
          return category && category.name.toLowerCase().includes('transfer');
        });
      } else {
        filtered = filtered.filter(t => t.type === filterType);
      }
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(t =>
        t.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.amount.toString().includes(searchTerm)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category_id === selectedCategory);
    }

    // Date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const transDate = (t) => parseLocalDate(t.date);

      switch (dateFilter) {
        case 'today':
          filtered = filtered.filter(t =>
            transDate(t).toDateString() === now.toDateString()
          );
          break;
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          filtered = filtered.filter(t => transDate(t) >= weekAgo);
          break;
        case 'month':
          filtered = filtered.filter(t =>
            transDate(t).getMonth() === now.getMonth() &&
            transDate(t).getFullYear() === now.getFullYear()
          );
          break;
        case 'year':
          filtered = filtered.filter(t =>
            transDate(t).getFullYear() === now.getFullYear()
          );
          break;
        default:
          break;
      }
    }

    // Sorting
    switch (sortBy) {
      case 'date-desc':
        filtered.sort((a, b) => {
          const dateA = a.date ? a.date.split('T')[0] : '';
          const dateB = b.date ? b.date.split('T')[0] : '';
          if (dateB !== dateA) return dateB.localeCompare(dateA);
          const timeA = new Date(a.created_at || a.date).getTime() || 0;
          const timeB = new Date(b.created_at || b.date).getTime() || 0;
          return timeB - timeA;
        });
        break;
      case 'date-asc':
        filtered.sort((a, b) => {
          const dateA = a.date ? a.date.split('T')[0] : '';
          const dateB = b.date ? b.date.split('T')[0] : '';
          if (dateA !== dateB) return dateA.localeCompare(dateB);
          const timeA = new Date(a.created_at || a.date).getTime() || 0;
          const timeB = new Date(b.created_at || b.date).getTime() || 0;
          return timeA - timeB;
        });
        break;
      case 'amount-desc':
        filtered.sort((a, b) => b.amount - a.amount);
        break;
      case 'amount-asc':
        filtered.sort((a, b) => a.amount - b.amount);
        break;
      default:
        break;
    }

    return filtered;
  };

  const groupByDate = (transactions) => {
    const groups = {};
    transactions.forEach(t => {
      const date = parseLocalDate(t.date);
      const key = date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      if (!groups[key]) groups[key] = [];
      groups[key].push(t);
    });
    return groups;
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };


  const filteredTransactions = getFilteredTransactions();
  const groupedTransactions = groupByDate(filteredTransactions);
  const totalIncome = filteredTransactions.filter(t => t.type === 'income').reduce((sum, t) => sum + t.amount, 0);
  const totalExpense = filteredTransactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + t.amount, 0);
  const totalSavings = filteredTransactions.filter(t => t.type === 'saving').reduce((sum, t) => sum + t.amount, 0);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-lg text-slate-600">Cargando transacciones...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const handleDuplicate = (transaction) => {
    const duplicated = {
      ...transaction,
      id: undefined,
      date: getTodayLocalDateString(),
      description: transaction.description ? `${transaction.description}` : ''
    };
    setPreselectedType(transaction.type);
    setEditingTransaction(duplicated);
    setIsDuplicate(true);
    setIsModalOpen(true);
  };

  const renderDeleteConfirmDialog = () => (
    <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <DialogContent className="max-w-md p-6 rounded-3xl">
        <DialogHeader>
          <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mb-2">
            <Trash2 className="w-6 h-6" />
          </div>
          <DialogTitle className="text-lg font-black text-slate-900">
            ¿Eliminar transacción?
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500 mt-1">
            Esta acción no se puede deshacer. Se eliminará la transacción
            {transactionToDelete?.description ? ` "${transactionToDelete.description}"` : ''} por {transactionToDelete?.amount ? formatCurrency(transactionToDelete.amount) : ''}.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setDeleteConfirmOpen(false);
              setTransactionToDelete(null);
            }}
            className="rounded-2xl h-11 px-5 text-sm font-bold text-slate-600"
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={confirmDelete}
            className="rounded-2xl h-11 px-6 text-sm font-bold bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-500/20"
          >
            Eliminar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (isMobile) {
    return (
      <Layout>
        <MobileTransactions 
          transactions={transactions}
          categories={categories}
          accounts={accounts}
          user={user}
          onEdit={(t) => handleOpenModal(t.type, t)}
          onDuplicate={handleDuplicate}
          onDelete={handleDelete}
        />
        {/* Transaction Modal */}
        <TransactionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSuccess={handleTransactionSuccess}
          preselectedType={preselectedType}
          editingTransaction={editingTransaction}
          isDuplicate={isDuplicate}
        />
        {/* Transfer Modal */}
        <TransferModal
          isOpen={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          onSuccess={() => {
            fetchTransactions();
            fetchCategories();
          }}
        />
        {renderDeleteConfirmDialog()}
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8 px-4 py-6 md:px-6 md:py-8 animate-page-enter">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Transacciones</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-3">Gestiona tus ingresos, gastos y ahorros</p>
          </div>
          <div className="flex gap-3 flex-col sm:flex-row w-full sm:w-auto">
            <Button
              onClick={() => setIsTransferModalOpen(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Nueva Transferencia
            </Button>
            <Button
              onClick={() => handleOpenModal('expense')}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nueva Transacción
            </Button>
          </div>
        </div>

        {/* Filters Section */}
        <Card className="p-5 md:p-6 bg-white/80 backdrop-blur-sm border-slate-200">
          <div className="space-y-5">
            {/* Quick Filters - Móvil: wrap, Desktop: puede mantener horizontal */}
            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={() => setFilterType('all')}
                variant={filterType === 'all' ? 'default' : 'outline'}
                className={`${filterType === 'all' ? 'bg-slate-900' : ''}`}
                size="sm"
              >
                Todas
              </Button>
              <Button
                onClick={() => setFilterType('income')}
                variant={filterType === 'income' ? 'default' : 'outline'}
                className={`${filterType === 'income' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                size="sm"
              >
                <ArrowUpRight className="w-4 h-4 mr-1" />
                Ingresos
              </Button>
              <Button
                onClick={() => setFilterType('expense')}
                variant={filterType === 'expense' ? 'default' : 'outline'}
                className={`${filterType === 'expense' ? 'bg-red-600 hover:bg-red-700' : ''}`}
                size="sm"
              >
                <ArrowDownRight className="w-4 h-4 mr-1" />
                Gastos
              </Button>
              <Button
                onClick={() => setFilterType('saving')}
                variant={filterType === 'saving' ? 'default' : 'outline'}
                className={`${filterType === 'saving' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                size="sm"
              >
                <PiggyBank className="w-4 h-4 mr-1" />
                Ahorros
              </Button>
              <Button
                onClick={() => setFilterType('transfer')}
                variant={filterType === 'transfer' ? 'default' : 'outline'}
                className={`${filterType === 'transfer' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                size="sm"
              >
                <ArrowRightLeft className="w-4 h-4 mr-1" />
                Transferencias
              </Button>
            </div>

            {/* Advanced Filters - Stack completo en móvil */}
            <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-3">
              {/* Search */}
              <div className="relative sm:col-span-2 lg:col-span-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full"
                />
              </div>

              {/* Date Filter */}
              <Select value={dateFilter} onValueChange={setDateFilter}>
                <SelectTrigger className="w-full">
                  <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Fecha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las fechas</SelectItem>
                  <SelectItem value="today">Hoy</SelectItem>
                  <SelectItem value="week">Última semana</SelectItem>
                  <SelectItem value="month">Este mes</SelectItem>
                  <SelectItem value="year">Este año</SelectItem>
                </SelectContent>
              </Select>

              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full">
                  <Filter className="w-4 h-4 mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.icon} {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full">
                  <TrendingUp className="w-4 h-4 mr-2 flex-shrink-0" />
                  <SelectValue placeholder="Ordenar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="date-desc">Más reciente</SelectItem>
                  <SelectItem value="date-asc">Más antigua</SelectItem>
                  <SelectItem value="amount-desc">Mayor monto</SelectItem>
                  <SelectItem value="amount-asc">Menor monto</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Summary & Clear - Móvil optimizado */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t">
              <div className="text-sm text-slate-600">
                <span className="font-medium">{filteredTransactions.length}</span> de {transactions.length} transacciones
              </div>
              {(searchTerm || dateFilter !== 'all' || selectedCategory !== 'all' || filterType !== 'all') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearchTerm('');
                    setDateFilter('all');
                    setSelectedCategory('all');
                    setFilterType('all');
                  }}
                  className="w-full sm:w-auto"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Export Buttons - Visible para todos, bloqueado si es Free/No-Pro */}
        {filteredTransactions.length > 0 && (
          <Card className="p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Gem className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Exportar Reportes</h3>
                      {user?.subscription_tier !== 'pro' && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-200">
                          <Lock className="w-2.5 h-2.5" /> Plan Pro
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-slate-600 truncate">Descarga tus transacciones filtradas</p>
                  </div>
                </div>
                {user?.subscription_tier !== 'pro' && (
                  <Button
                    size="sm"
                    onClick={() => navigate('/pricing')}
                    className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8 px-3 rounded-lg shadow-sm"
                  >
                    <Lock className="w-3 h-3 mr-1" />
                    Mejorar a Pro
                  </Button>
                )}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  onClick={() => {
                    if (user?.subscription_tier !== 'pro') {
                      showToast.error('La exportación de reportes PDF requiere el Plan Pro');
                      navigate('/pricing');
                      return;
                    }
                    exportToPDF(filteredTransactions, categories, null, null, user.username);
                  }}
                  variant="outline"
                  size="sm"
                  className={`bg-white hover:bg-red-50 border-red-300 text-red-700 text-xs ${user?.subscription_tier !== 'pro' ? 'border-purple-200/70 bg-purple-50/20' : ''}`}
                >
                  <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                  <span className="hidden sm:inline">PDF</span>
                  {user?.subscription_tier !== 'pro' && <Lock className="w-3 h-3 ml-1 text-purple-600" />}
                </Button>
                <Button
                  onClick={() => {
                    if (user?.subscription_tier !== 'pro') {
                      showToast.error('La exportación de reportes Excel requiere el Plan Pro');
                      navigate('/pricing');
                      return;
                    }
                    exportToExcel(filteredTransactions, categories);
                  }}
                  variant="outline"
                  size="sm"
                  className={`bg-white hover:bg-green-50 border-green-300 text-green-700 text-xs ${user?.subscription_tier !== 'pro' ? 'border-purple-200/70 bg-purple-50/20' : ''}`}
                >
                  <FileSpreadsheet className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Excel</span>
                  {user?.subscription_tier !== 'pro' && <Lock className="w-3 h-3 ml-1 text-purple-600" />}
                </Button>
                <Button
                  onClick={() => {
                    if (user?.subscription_tier !== 'pro') {
                      showToast.error('La exportación de reportes CSV requiere el Plan Pro');
                      navigate('/pricing');
                      return;
                    }
                    exportToCSV(filteredTransactions, categories);
                  }}
                  variant="outline"
                  size="sm"
                  className={`bg-white hover:bg-blue-50 border-blue-300 text-blue-700 text-xs ${user?.subscription_tier !== 'pro' ? 'border-purple-200/70 bg-purple-50/20' : ''}`}
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-1" />
                  <span className="hidden sm:inline">CSV</span>
                  {user?.subscription_tier !== 'pro' && <Lock className="w-3 h-3 ml-1 text-purple-600" />}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Transactions List - Grouped by Date */}
        <div className="space-y-4">
          {Object.keys(groupedTransactions).length === 0 ? (
            <Card className="p-12 bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
              <div className="text-center">
                <p className="text-xl text-slate-600 mb-2 font-semibold">
                  No se encontraron transacciones
                </p>
                <p className="text-slate-500">
                  {searchTerm || dateFilter !== 'all' || selectedCategory !== 'all'
                    ? 'Intenta cambiar los filtros'
                    : 'Comienza agregando tu primera transacción'}
                </p>
              </div>
            </Card>
          ) : (
            Object.entries(groupedTransactions)
              .sort(([dateKeyA, transA], [dateKeyB, transB]) => {
                // Ordenar grupos según sortBy
                if (sortBy === 'date-desc' || sortBy === 'date-asc') {
                  // Ordenar por fecha de la primera transacción de cada grupo
                  const dateA = parseLocalDate(transA[0].date);
                  const dateB = parseLocalDate(transB[0].date);
                  return sortBy === 'date-desc' ? (dateB - dateA) : (dateA - dateB);
                } else if (sortBy === 'amount-desc' || sortBy === 'amount-asc') {
                  // Ordenar por monto de la primera transacción de cada grupo
                  const amountA = transA[0].amount;
                  const amountB = transB[0].amount;
                  return sortBy === 'amount-desc' ? (amountB - amountA) : (amountA - amountB);
                }
                return 0;
              })
              .map(([dateKey, dayTransactions]) => {
                const isExpanded = expandedGroups[dateKey] !== false;
                const dayTotal = dayTransactions.reduce((sum, t) => {
                  if (t.type === 'income') return sum + t.amount;
                  if (t.type === 'expense') return sum - t.amount;
                  return sum;
                }, 0);

                return (
                  <Card key={dateKey} className="overflow-hidden border-slate-200">
                    {/* Date Header - Móvil optimizado */}
                    <button
                      onClick={() => toggleGroup(dateKey)}
                      className="w-full px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-150 transition-all flex items-center justify-between"
                    >
                      <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                        <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 flex-shrink-0" />
                        <div className="text-left min-w-0 flex-1">
                          <h3 className="font-bold text-slate-900 text-sm sm:text-base truncate">{dateKey}</h3>
                          <p className="text-xs sm:text-sm text-slate-600">{dayTransactions.length} transacciones</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
                        <span className={`font-bold text-sm sm:text-lg ${dayTotal >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                          {formatCurrency(Math.abs(dayTotal))}
                        </span>
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                        ) : (
                          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600" />
                        )}
                      </div>
                    </button>

                    {/* Transactions */}
                    {isExpanded && (
                      <div className="divide-y divide-slate-200">
                        {dayTransactions
                          .sort((a, b) => {
                            // Sort by created_at descending (newest first within the day)
                            return new Date(b.created_at) - new Date(a.created_at);
                          })
                          .map(transaction => {
                            const category = categories.find(c => c.id === transaction.category_id);
                            const typeConfig = {
                              income: { icon: ArrowUpRight, color: 'text-green-600', bg: 'bg-green-50', border: 'border-green-200' },
                              expense: { icon: ArrowDownRight, color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200' },
                              saving: { icon: PiggyBank, color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-200' }
                            }[transaction.type];
                            const Icon = typeConfig.icon;

                            return (
                              <div
                                key={transaction.id}
                                className="p-5 md:p-6 hover:bg-slate-50 transition-colors group relative rounded-lg"
                              >
                                {/* Layout Desktop y Móvil diferente */}
                                <div className="flex flex-col gap-4 sm:pr-32">
                                  {/* Móvil: Stack vertical */}
                                  <div className="flex items-start gap-4">
                                    {/* Icon */}
                                    <div className={`p-2.5 rounded-lg ${typeConfig.bg} ${typeConfig.border} border flex-shrink-0`}>
                                      <Icon className={`w-5 h-5 ${typeConfig.color}`} />
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-slate-900 text-base leading-tight mb-1">
                                        {transaction.description || 'Sin descripción'}
                                      </h4>
                                      {(() => {
                                        const bal = transactionBalancesMap?.[transaction.id] || 
                                                    (transaction.previous_balance !== null && transaction.previous_balance !== undefined && transaction.new_balance !== null && transaction.new_balance !== undefined
                                                      ? { prev: transaction.previous_balance, next: transaction.new_balance }
                                                      : null);
                                        let prev = bal?.prev ?? transaction.previous_balance;
                                        let curr = bal?.next ?? transaction.new_balance;
                                        if ((prev === undefined || prev === null || curr === undefined || curr === null) && transaction.notes) {
                                          const match = transaction.notes.match(/saldo anterior:\s*([-$0-9.,]+)[,\s→-]+(?:(?:nuevo saldo|saldo despu[ée]s):\s*)?([-$0-9.,]+)/i);
                                          if (match) {
                                            prev = parseFloat(match[1].replace(/[$]/g, ''));
                                            curr = parseFloat(match[2].replace(/[$]/g, ''));
                                          }
                                        }
                                        const acc = accounts.find(a => a.id === transaction.account_id);
                                        const isCredit = acc?.type === 'credit_card';
                                        const isExpense = transaction.type === 'expense';
                                        const isIncome = transaction.type === 'income';
                                        const amt = parseFloat(transaction.amount) || 0;

                                        let displayPrev = prev !== undefined && !isNaN(prev) ? prev : null;
                                        let displayNext = curr !== undefined && !isNaN(curr) ? curr : null;

                                        if (isCredit && displayPrev !== null) {
                                          const prevDebt = Math.abs(displayPrev);
                                          displayPrev = prevDebt;
                                          if (transaction.is_balance_adjustment || /ajuste de saldo/i.test(transaction.description || '')) {
                                            displayNext = displayNext !== null ? Math.abs(displayNext) : prevDebt;
                                          } else if (transaction.is_breakdown || (transaction.is_retention && transaction.retention_status === 'released')) {
                                            displayNext = prevDebt;
                                          } else if (isExpense) {
                                            displayNext = prevDebt + amt; // Compra AUMENTA la deuda
                                          } else if (transaction.type === 'income') {
                                            displayNext = Math.max(0, prevDebt - amt); // Abono REDUCE la deuda
                                          } else {
                                            displayNext = prevDebt;
                                          }
                                        }

                                        const nextColor = isExpense ? 'text-rose-500/90 dark:text-rose-400/90' : 'text-emerald-600/90 dark:text-emerald-400/90';
                                        return (
                                          <div className="mb-2 flex items-center gap-1.5 text-[11px] text-slate-400 dark:text-slate-500 bg-slate-50/70 dark:bg-slate-800/40 px-2.5 py-0.5 rounded-lg w-fit border border-slate-100 dark:border-slate-700/50">
                                            <span className="font-normal opacity-80">Ant:</span>
                                            <span className="font-medium text-slate-600 dark:text-slate-400">
                                              {displayPrev !== null ? formatCurrency(displayPrev) : 'N/D'}
                                            </span>
                                            <span className="text-slate-300 dark:text-slate-600 font-normal">→</span>
                                            <span className="font-normal opacity-80">Después:</span>
                                            <span className={`font-semibold ${nextColor}`}>
                                              {displayNext !== null ? formatCurrency(displayNext) : 'N/D'}
                                            </span>
                                          </div>
                                        );
                                      })()}
                                      <div className="flex flex-wrap items-center gap-2 text-sm">
                                        {category && (
                                          <span className="text-slate-600 flex items-center gap-1">
                                            {category.icon} {category.name}
                                          </span>
                                        )}
                                        {(() => {
                                          // Si tiene account_id, mostrar el nombre de la cuenta
                                          if (transaction.account_id) {
                                            const account = accounts.find(acc => acc.id === transaction.account_id);
                                            if (account) {
                                              return (
                                                <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-100 rounded">
                                                  {account.name}
                                                </span>
                                              );
                                            }
                                          }
                                          // Si no tiene account_id, mostrar payment_method
                                          if (transaction.payment_method) {
                                            return (
                                              <span className="text-xs text-slate-500 px-2 py-0.5 bg-slate-100 rounded">
                                                {transaction.payment_method}
                                              </span>
                                            );
                                          }
                                          return null;
                                        })()}

                                        {transaction.items_breakdown?.length > 0 && (
                                          <button
                                            type="button"
                                            onClick={() => setExpandedItemsId(expandedItemsId === transaction.id ? null : transaction.id)}
                                            className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-bold rounded-full flex items-center gap-1 hover:bg-indigo-100 transition-colors cursor-pointer"
                                          >
                                            <Package className="w-3 h-3 text-indigo-600" />
                                            <span>{transaction.items_breakdown.length} artículos</span>
                                            {expandedItemsId === transaction.id ? <ChevronUp className="w-2.5 h-2.5" /> : <ChevronDown className="w-2.5 h-2.5" />}
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    {/* Amount - Móvil: en la misma línea pero a la derecha */}
                                    <div className="text-right flex-shrink-0">
                                      <p className={`text-lg font-bold ${typeConfig.color} whitespace-nowrap`}>
                                        {formatCurrency(transaction.amount)}
                                      </p>
                                    </div>
                                  </div>

                                  {/* Desglose desplegado sin editar */}
                                  {expandedItemsId === transaction.id && transaction.items_breakdown?.length > 0 && (
                                    <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 w-full max-w-lg">
                                      <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                                        Artículos de esta compra ({transaction.items_breakdown.length})
                                      </p>
                                      <div className="space-y-1">
                                          {transaction.items_breakdown.map((item, idx) => (
                                            <div key={item.id || idx} className="flex items-center justify-between text-xs py-1 px-2.5 bg-white rounded-lg border border-slate-100">
                                              <div className="flex items-center gap-1.5 truncate mr-2">
                                                <span className="text-slate-700 font-medium truncate">{item.name}</span>
                                                {item.category_name && (
                                                  <span className="text-[9px] px-1.5 py-0.2 bg-slate-100 text-slate-600 rounded font-bold shrink-0">
                                                    {item.category_name}
                                                  </span>
                                                )}
                                              </div>
                                              <span className="font-bold text-slate-900 shrink-0">${parseFloat(item.price).toFixed(2)}</span>
                                            </div>
                                          ))}
                                      </div>
                                    </div>
                                  )}

                                  {/* Actions - Móvil: botones completos debajo */}
                                  <div className="flex items-center gap-2 sm:hidden border-t pt-3">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleOpenModal(transaction.type, transaction)}
                                      className="flex-1 text-xs h-9"
                                    >
                                      <Edit className="w-3.5 h-3.5 mr-1.5" />
                                      Editar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleDuplicate(transaction)}
                                      className="flex-1 text-xs h-9"
                                    >
                                      <Copy className="w-3.5 h-3.5 mr-1.5" />
                                      Duplicar
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleDelete(transaction)}
                                      className="flex-1 text-xs h-9 text-red-600 border-red-300 hover:bg-red-50"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                      Eliminar
                                    </Button>
                                  </div>

                                  {/* Actions - Desktop: hover */}
                                  <div className="hidden sm:flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity absolute right-3 top-1/2 -translate-y-1/2 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 p-1">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleOpenModal(transaction.type, transaction)}
                                      className="hover:bg-blue-100 h-8 w-8 p-0"
                                      title="Editar"
                                    >
                                      <Edit className="w-4 h-4 text-blue-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDuplicate(transaction)}
                                      className="hover:bg-purple-100 h-8 w-8 p-0"
                                      title="Duplicar"
                                    >
                                      <Copy className="w-4 h-4 text-purple-600" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleDelete(transaction)}
                                      className="hover:bg-red-100 h-8 w-8 p-0"
                                      title="Eliminar"
                                    >
                                      <Trash2 className="w-4 h-4 text-red-600" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </Card>
                );
              })
          )}
        </div>

        {/* Summary Footer - Móvil optimizado */}
        {filteredTransactions.length > 0 && (
          <Card className="p-4 sm:p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Resumen del Periodo Filtrado</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="p-3 sm:p-4 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                  <span className="text-xs sm:text-sm text-slate-300">Ingresos</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-green-400">{formatCurrency(totalIncome)}</p>
              </div>
              <div className="p-3 sm:p-4 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownRight className="w-4 h-4 sm:w-5 sm:h-5 text-red-400" />
                  <span className="text-xs sm:text-sm text-slate-300">Gastos</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-red-400">{formatCurrency(totalExpense)}</p>
              </div>
              <div className="p-3 sm:p-4 bg-white/10 backdrop-blur-sm rounded-lg sm:rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <PiggyBank className="w-4 h-4 sm:w-5 sm:h-5 text-blue-400" />
                  <span className="text-xs sm:text-sm text-slate-300">Ahorros</span>
                </div>
                <p className="text-xl sm:text-2xl font-bold text-blue-400">{formatCurrency(totalSavings)}</p>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Transaction Modal */}
      <TransactionModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleTransactionSuccess}
        preselectedType={preselectedType}
        editingTransaction={editingTransaction}
        isDuplicate={isDuplicate}
      />

      {/* Transfer Modal */}
      <TransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        onSuccess={() => {
          fetchTransactions();
          fetchCategories();
        }}
      />
      {renderDeleteConfirmDialog()}
    </Layout>
  );
};

export default Transactions;